import { ErrorTranslationKeys } from '../../../api/models/TranslationKeys';
import { Lightning, Log, Router } from '@lightningjs/sdk';
import { ActionMethod, AssetType, CmsProperty, PageType, StreamType } from '../../../api/models/Shared.types';
import { DetailPage } from '../../../pages/detail/DetailPage';
import { resolvePlay } from './PlayActionResolver';
import { VodApi } from '../../../api/VodApi';
import { PriceApi } from '../../../api/price';
import { VodAsset } from '../../detail/vod/VodAsset';
import { OttStream, VodStreamRights } from '../../detail/vod/VodStreamRights';
import { VodStreamInfo } from '../../player/streamInfo/VodStreamInfo';
import { StartPositionType } from '../../player/streamInfo/StreamInfo';
import { EpgListPage } from '../../../pages/content/listPage/EpgListPage';
import { listFilterType } from '../../../pages/content/listPage/FilterState';
import { RecordingFolderPage } from '../../../pages/content/recording/RecordingFolderPage';
import { IAssetAction } from '../../../api/models/AndromeTV.types';
import { Backend } from '../../../api/Backend';
import { CmsCard } from '../../../components/cms/CmsCard';
import { ChannelManager } from '../../../api/ChannelManager';

/** interface IAssetActionExtra */
interface IAssetActionExtra {
  adult?: boolean;
  seriesId?: string;
  channelId?: string;
  pageType?: PageType;
}

export class CmsActionResolver {
  static doAction(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (action.extra?.adult) {
      Log.error('[CmsActionResolver]', 'action extra adult, not allowed');
      caller.fireAncestors(
        '$showMessage',
        '',
        backend.translations.forKey(ErrorTranslationKeys.error_adult_mode_required),
        undefined
      );
      return;
    }
    if (!action.method) {
      Log.error('[CmsActionResolver]', 'no method passed');
      caller.fireAncestors(
        '$showMessage',
        backend.translations.forKey(ErrorTranslationKeys.error_general),
        backend.translations.forKey(ErrorTranslationKeys.error_title),
        undefined
      );
    } else {
      this._doHandle(caller, action, backend)?.catch((err: unknown) => {
        Log.error('[CmsActionResolver]', 'error', err);
        caller.fireAncestors(
          '$showMessage',
          backend.translations.forKey(ErrorTranslationKeys.error_general),
          backend.translations.forKey(ErrorTranslationKeys.error_title),
          undefined
        );
      });
    }
  }

  static _doHandle(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
    switch (action.method) {
      case ActionMethod.DETAIL:
        this._doDetail(caller, action, backend);
        return Promise.resolve();
      case ActionMethod.PLAY:
        return this._doPlay(caller, action, backend);
      case ActionMethod.PLAY_TRAILER:
        return this._doPlayTrailer(caller, action, backend);
      case ActionMethod.JUMP_TO_LIST:
        return this._doJumpToList(caller, action, backend);
      case ActionMethod.FAVOURITE:
        return this._doFavourite(caller, action, backend);
      case ActionMethod.OPEN_FOLDER:
        this._doOpenFolder(caller, action, backend);
        return Promise.resolve();
    }
  }

  static _doDetail(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
    switch (action.type) {
      case AssetType.VOD:
      case AssetType.PROGRAM:
      case AssetType.SERIES:
      case AssetType.CHANNEL:
      case AssetType.SCHEDULE:
      case AssetType.RECORDING:
      case AssetType.BUNDLE:
        caller.fireAncestors('$openModal', DetailPage, {
          backend: backend,
          assetType: action.type,
          assetId: action.id,
          modalOwner: caller,
        });
        return;
      case AssetType.ARTICLE:
        // TODO: implement Article
        Log.warn('[CmsActionResolver] DetailMethod AssetType.ARTICLE not implemented', action, caller, backend);
        return;
      case AssetType.PRODUCT:
      case AssetType.SUBSCRIPTION:
      case AssetType.PAGE:
        Log.info('[CmsActionResolver]', '[DetailMethod]', 'action: ', action, caller, backend);
        caller.fireAncestors('$CMSActionOpenPage', action, backend);
    }
  }

  static _doPlay(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
    if (backend.isAuthorized()) {
      return resolvePlay(caller, action, backend);
    } else {
      //TODO start login sequence
      Log.warn('[CmsActionResolver]', '[doPlay]', 'not logged in');
    }
  }

  static _doPlayTrailer(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
    backend.cachedQuoteApi.bypassCache(true);
    return Promise.all([
      VodApi.asset(backend, action.id),
      backend.cachedQuoteApi.list(backend),
      PriceApi.forVod(backend, action.id, {
        entitlementId: backend.sessionInfo?.device.entitlementId,
        catalogId: backend.deviceProfile?.profile.catalog,
      }),
    ]).then(([asset, quotes, price]) => {
      const vodModel = new VodAsset(asset);
      const streamRights = new VodStreamRights(vodModel.assetId, quotes, price);

      if (!(streamRights.trailerStreams.length > 0)) {
        Log.error('No trailer found');
        return;
      }

      const trailerStream = streamRights.trailerStreams.find(
        t => t.type === StreamType.FVOD || (t.type === StreamType.SVOD && backend.isAuthorized())
      );

      if (!trailerStream) {
        if (streamRights.trailerStreams.some(t => t.type === StreamType.SVOD)) {
          //todo show login
        }
        Log.error(' no watchable trailer found');
        return;
      }

      const streamInfo = new VodStreamInfo(
        vodModel,
        new OttStream(trailerStream, []),
        undefined,
        StartPositionType.START
      );
      Router.navigate('play', { streamInfo });
    });
  }

  static _doJumpToList(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
    if (action.type !== AssetType.CHANNEL) {
      Log.warn('[doJumpToList]', 'wrong actionType');
      return Promise.resolve();
    }
    return ChannelManager.channels(backend).then(channels => {
      const channel = channels.find(ch => ch.channelId === action.id);
      if (!channel) {
        Log.error('[doJumpToList]', 'channel not found');
        return;
      }

      const params = {
        backend: backend,
        filterInit: {
          [listFilterType.channel]: { text: channel.text.title, value: channel },
        },
        isReplay: (caller as CmsCard).data?.pageProperties.includes(CmsProperty.FocusPast),
      };
      // console.log('[CmsActionResolver] _doJumpToList', this, params);
      // console.log('[CmsActionResolver] _doJumpToList', action);
      caller.fireAncestors('$updateMenu', PageType.List);
      caller.fireAncestors('$openPage', EpgListPage, params);
    });
  }

  static _doFavourite(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
    return backend.cachedFavoriteApi
      .getFavorite(backend, { id: action.id, type: action.type as AssetType })
      .then(currentState => {
        const removeFav = !!currentState;
        if (removeFav) {
          return backend.cachedFavoriteApi
            .deleteFavorite(backend, { id: action.id, type: action.type as AssetType })
            .catch(() => {
              Log.error('[_doFavourite]', 'error removing favourite');
            });
        } else {
          const extra = action.extra as IAssetActionExtra;
          return backend.cachedFavoriteApi
            .setFavorite(
              backend,
              {
                id: action.id,
                type: action.type as AssetType,
              },
              extra.adult ?? false
            )
            .catch(() => {
              Log.error('[_doFavourite]', 'error adding favourite');
            });
        }
      });
  }

  static _doOpenFolder(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
    const extra = action.extra as IAssetActionExtra;
    caller.fireAncestors(
      '$openPage',
      RecordingFolderPage,
      {
        backend: backend,
        folderInfo: {
          seriesId: extra?.seriesId,
          channelId: extra?.channelId,
          pageType: extra?.pageType,
        },
      },
      true
    );
    return;
  }
}
